import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { UserPlus, Loader2 } from "lucide-react";

const FIELDS = [
  ["name", "Full name *", "sm:col-span-2"], ["property_address", "Property address", "sm:col-span-2"],
  ["city", "City"], ["state", "State (e.g. FL)"], ["zip_code", "ZIP"], ["phone", "Phone"],
  ["email", "Email", "sm:col-span-2"], ["mailing_address", "Mailing address (if different)", "sm:col-span-2"],
];
const TYPES = ["Purchase", "Refinance", "HELOC", "Reverse Mortgage"];
const cell = (v) => `"${String(v || "").replace(/"/g, '""')}"`;

export const ManualLead = ({ onDone }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({});
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const save = async () => {
    if (!f.name?.trim() || !(f.property_address?.trim() || f.phone?.trim() || f.email?.trim())) {
      toast.error("Enter a name plus an address, phone or email"); return;
    }
    const keys = [...FIELDS.map(([k]) => k), "opportunity_type"];
    const csv = `${keys.join(",")}\n${keys.map((k) => cell(f[k])).join(",")}\n`;
    setBusy(true);
    try {
      const { data } = await api.post("/prospects/import", { csv_data: csv });
      if (data.imported) {
        toast.success("Lead added - looking up property value, equity, rate and age now"); setF({}); setOpen(false); onDone?.();
        [15000, 35000].forEach((ms) => setTimeout(() => onDone?.(), ms));
      }
      else toast.error(data.suppressed ? "This contact is on the suppression / Do Not Contact list" : data.errors?.[0] || "Lead could not be added");
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };
  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} data-testid="manual-lead-btn"><UserPlus className="mr-2 h-4 w-4" /> Add lead</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto" data-testid="manual-lead-dialog">
          <DialogHeader>
            <DialogTitle>Add a lead manually (free)</DialogTitle>
            <DialogDescription>Free. After you save, Elite Leads looks up ATTOM, homes for sale and AvaData to fill in any blank details (value, equity, rate, age) and AI-scores the loan type. Needs a name plus an address, phone or email.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {FIELDS.map(([k, label, span]) => (
              <div key={k} className={span || ""}><Label className="text-xs">{label}</Label>
                <Input className="mt-1" value={f[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`manual-lead-${k}`} /></div>
            ))}
            <div className="sm:col-span-2"><Label className="text-xs">Loan type</Label>
              <Select value={f.opportunity_type || ""} onValueChange={(v) => set("opportunity_type", v)}>
                <SelectTrigger className="mt-1" data-testid="manual-lead-type"><SelectValue placeholder="Choose (optional)" /></SelectTrigger>
                <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <DialogFooter>
            <Button onClick={save} disabled={busy} className="bg-slate-900 hover:bg-slate-800" data-testid="manual-lead-save">
              {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <UserPlus className="mr-1.5 h-4 w-4" />} Add lead
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
